<template>
  <v-navigation-drawer v-bind="$attrs" :mini-variant.sync="mini">
    <template v-slot:prepend>
      <v-toolbar flat dense v-if="!mini">
        <v-toolbar-title class="subtitle-1">Sections manager</v-toolbar-title>

        <v-spacer></v-spacer>

        <v-btn icon small @click="mini = !mini">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
      </v-toolbar>

      <v-btn class="mt-2" icon small @click="mini = !mini" v-else>
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>

      <v-divider v-show="!mini"></v-divider>

      <v-toolbar flat dense v-if="tabs.length" v-show="!mini">
        <v-tabs v-model="currentTab" color="secondary">
          <v-tab v-for="tab in tabs" :key="tab" small>{{ tab }}</v-tab>
        </v-tabs>
      </v-toolbar>

      <v-divider v-if="tabs.length" v-show="!mini"></v-divider>
    </template>

    <v-card-text
      v-show="!mini"
      v-if="pageBySlug.sections && !pageBySlug.sections.length"
      >This page has no sections. Go to builder to add sections.</v-card-text
    >

    <v-card-text v-show="!mini" v-else-if="!drawer.itemID"
      >Select a section to edit.</v-card-text
    >

    <v-form v-model="valid" v-show="!mini" v-else>
      <v-card
        flat
        tile
        :loading="$apollo.loading || drawer.loading"
        :disabled="$apollo.loading || drawer.loading"
      >
        <v-card-text class="pa-0">
          <v-tabs-items v-model="currentTab">
            <v-tab-item eager v-for="tab in tabs" :key="tab">
              <div class="editor-form-fields">
                <div
                  class="uncategorized-fields px-4 pt-6"
                  v-if="
                    Object.keys(getFieldsByCategory(formFields[tab])).length ||
                      tab == 'advanced'
                  "
                >
                  <div v-if="tab == 'advanced'">
                    <div>
                      <field-label
                        :field="{
                          label: 'Custom ID',
                          required: true,
                          hint:
                            'Set the element ID of the section. This is used for custom CSS styling for the section.'
                        }"
                      ></field-label>
                      <v-text-field
                        v-model="currentSection.element_id"
                        outlined
                        dense
                        v-track-changes-once
                        @change="slugifyElementID"
                        :rules="[$store.state.editor.validation.rules.required]"
                      ></v-text-field>
                    </div>

                    <div>
                      <field-label
                        :field="{
                          label: 'Custom classes',
                          hint:
                            'Set the element classes of the section. Multiple classes can be added by separating them with space. This is used for custom CSS styling for the section.'
                        }"
                      ></field-label>
                      <v-text-field
                        v-model="currentSection.element_classes"
                        outlined
                        dense
                        v-track-changes-once
                      ></v-text-field>
                    </div>

                    <div>
                      <field-label
                        :field="{
                          label: 'Global section',
                          required: true,
                          hint:
                            'Set as a global section. Global sections are visible on all pages. Changes made will be applied synchronously.'
                        }"
                      ></field-label>

                      <v-btn-toggle
                        group
                        class="d-block pb-5"
                        v-model="currentSection.global"
                        mandatory
                        v-track-changes-once
                      >
                        <v-btn small :value="false">No</v-btn>
                        <v-btn small :value="true">Yes</v-btn>
                      </v-btn-toggle>
                    </div>
                  </div>

                  <component
                    v-for="(field, index) in getFieldsByCategory(
                      formFields[tab]
                    )"
                    :key="index"
                    :is="`${field.type}-field`"
                    :field="field"
                    :value.sync="currentSection[tab][index]"
                    v-show="evaluateField(field.showOn, tab)"
                    v-track-changes-once
                  ></component>
                </div>

                <v-expansion-panels
                  accordion
                  hover
                  tile
                  flat
                  class="elevation-0"
                  focusable
                >
                  <v-expansion-panel
                    style="margin-right: 2px"
                    class="categorized-fields"
                    v-for="(category, index) in fieldCategories(
                      formFields[tab]
                    )"
                    :key="index"
                  >
                    <v-divider
                      v-if="
                        index == 0 &&
                          Object.keys(getFieldsByCategory(formFields[tab]))
                            .length
                      "
                    ></v-divider>

                    <v-expansion-panel-header
                      style="min-height:0"
                      class="subtitle-2"
                      >{{ category }}</v-expansion-panel-header
                    >

                    <v-expansion-panel-content color="transparent" class="pt-2">
                      <component
                        v-for="(field, index) in getFieldsByCategory(
                          formFields[tab],
                          category
                        )"
                        :key="index"
                        :is="`${field.type}-field`"
                        :field="field"
                        :value.sync="currentSection[tab][index]"
                        v-show="evaluateField(field.showOn, tab)"
                        v-track-changes-once
                      ></component>
                    </v-expansion-panel-content>

                    <v-divider></v-divider>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </v-card>
    </v-form>

    <template v-slot:append v-show="!mini">
      <v-toolbar flat dense v-if="!valid">
        <v-spacer></v-spacer>

        <span class="error--text"
          >Please make sure that all fields are valid.</span
        >
      </v-toolbar>

      <v-divider></v-divider>

      <v-toolbar flat dense>
        <v-spacer></v-spacer>
        <v-btn
          text
          class="mr-2"
          color="success"
          @click="save(false)"
          :disabled="!drawer.changed || !valid || drawer.loading"
          >Save</v-btn
        >

        <v-btn
          text
          class="mr-2"
          color="warning"
          @click="save(true)"
          :disabled="!drawer.changed || !valid || drawer.loading"
          >Save & close</v-btn
        >
        <v-btn
          text
          color="error"
          :disabled="drawer.loading"
          @click="closeEditor()"
          >Close</v-btn
        >
      </v-toolbar>
    </template>
  </v-navigation-drawer>
</template>

<script>
import FieldLabel from '@/components/editor/partials/FieldLabel'
import { activeTemplate } from '@/graphql/template.gql'
import { pageBySlug } from '@/graphql/page.gql'
import { updateSection } from '@/graphql/section.gql'
import { mapState, mapActions } from 'vuex'
import _ from 'lodash'

export default {
  name: 'SectionEditor',
  components: {
    FieldLabel
  },
  data: () => ({
    valid: true,
    mini: false,
    currentTab: null,
    formFields: {}
  }),
  computed: {
    ...mapState('editor', ['drawer']),
    currentSection() {
      if (!this.pageBySlug) {
        return {}
      } else if (!this.drawer.itemID && this.pageBySlug.sections.length) {
        this.drawer.itemID = this.pageBySlug.sections[0].id
      }

      // if current section is not found
      if (
        this.drawer.itemID &&
        !this.pageBySlug.sections.filter(
          section => section.id == this.drawer.itemID
        ).length
      ) {
        this.drawer.itemID = null
        return {}
      }

      return this.pageBySlug.sections.filter(
        section => section.id == this.drawer.itemID
      )[0]
    },
    tabs() {
      return Object.keys(this.formFields).filter(
        key => Object.keys(this.formFields[key]).length || key == 'advanced' // advanced is always visible
      )
    }
  },
  watch: {
    'drawer.itemID'() {
      this.setFormFields()
    }
  },
  apollo: {
    activeTemplate: {
      query: activeTemplate,
      result({ data }) {
        Object.entries(data.activeTemplate.design).forEach(([key, value]) => {
          document.documentElement.style.setProperty(`--${key}`, value)
        })
      }
    },
    pageBySlug: {
      query: pageBySlug,
      skip() {
        return !this.activeTemplate
      },
      variables() {
        return {
          data: {
            slug:
              this.$route.params.page ||
              this.activeTemplate.pages.filter(page => page.home)[0].slug,
            template_id: this.activeTemplate.id
          }
        }
      }
    }
  },
  methods: {
    ...mapActions('editor', ['closeEditor', 'saveChanges']),
    save(close) {
      this.saveChanges({
        mutation: updateSection,
        variables: {
          data: {
            id: this.currentSection.id,
            element_id: this.currentSection.element_id,
            element_classes: this.currentSection.element_classes,
            page_id: this.pageBySlug.id,
            content: this.currentSection.content,
            design: this.currentSection.design,
            layout: this.currentSection.layout,
            advanced: this.currentSection.advanced,
            global: this.currentSection.global
          }
        },
        messages: {
          success: 'Section successfully updated.'
        },
        refetch: true,
        close
      })
    },
    setFormFields() {
      if (this.currentSection.type) {
        let typeDirectory = _.chain(this.currentSection.type)
          .camelCase()
          .upperFirst()
          .value()

        import(
          '@/components/sections/types/' + typeDirectory + '/form-fields'
        ).then(module => {
          this.formFields = module.default

          // Set drawer changed to false after forFields are modified
          this.drawer.changed = false

          this.currentTab = null

          this.mini = false
        })
      } else {
        this.formFields = {}
      }
    },
    slugifyElementID() {
      this.currentSection.element_id = this.slugify(
        this.currentSection.element_id
      )
    },
    evaluateField(condition, tab) {
      // this method is used to check showOn property on editor fields
      if (!condition) {
        return true
      }

      switch (condition.operator) {
        case '>':
          return this.currentSection[tab][condition.field] > condition.value
        case '<':
          return this.currentSection[tab][condition.field] < condition.value
        case '>=':
          return this.currentSection[tab][condition.field] >= condition.value
        case '<=':
          return this.currentSection[tab][condition.field] <= condition.value
        case '==':
          return this.currentSection[tab][condition.field] == condition.value
        case '!=':
          return this.currentSection[tab][condition.field] != condition.value
        case '===':
          return this.currentSection[tab][condition.field] === condition.value
        case '!==':
          return this.currentSection[tab][condition.field] !== condition.value
        case 'isNull':
          return !this.currentSection[tab][condition.field]
        case 'notNull':
          return this.currentSection[tab][condition.field]
      }
    }
  },
  mounted() {
    if (this.drawer.itemID) {
      this.setFormFields()
    }
  }
}
</script>
